import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import MascotPlaceholder from "@/components/MascotPlaceholder";
import { X } from "@phosphor-icons/react";

const MASCOT_BY_KEY = {
  ready: "/assets/boardy-energico.png",
  curious: "/assets/boardy-curioso.png",
  worried: "/assets/boardy-calmo.png",
};

export default function QuizResult() {
  const { t, anonState } = useApp();
  const location = useLocation();
  const navigate = useNavigate();
  const profile = location.state?.profile || anonState.quiz_result;

  if (!profile) {
    return (
      <div className="max-w-2xl mx-auto p-8 text-center">
        <p className="text-ink-soft">{t.common.loading}</p>
        <Button className="rounded-full mt-4" onClick={() => navigate("/quiz")}>{t.quiz.title}</Button>
      </div>
    );
  }

  const mascotSrc = MASCOT_BY_KEY[profile.key] || "/assets/boardy-energico.png";

  return (
    <div className="max-w-2xl mx-auto px-5 py-10 space-y-8">
      {/* Exit control (no fake "save" — just a way to leave the screen) */}
      <div className="flex justify-end mb-2">
        <button
          data-testid="quiz-result-exit"
          aria-label={t.quiz.exit}
          onClick={() => navigate("/")}
          className="w-10 h-10 rounded-full border border-border bg-surface-card text-ink-soft hover:text-ink hover:bg-surface-alt transition-colors flex items-center justify-center"
        >
          <X size={18} weight="bold" />
        </button>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center mb-8"
      >
        <div className="flex justify-center mb-6">
          <MascotPlaceholder src={mascotSrc} />
        </div>
        <p className="text-xs uppercase tracking-widest text-terracotta font-semibold font-sans">
          {t.quiz.profileTitle}
        </p>
        <h1 data-testid="quiz-result-headline" className="font-display font-black text-3xl sm:text-4xl text-ink mt-2 leading-tight tracking-tight">
          {profile.headline}
        </h1>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.15 }}
        className="bg-surface-card rounded-3xl border border-border p-6 sm:p-8 shadow-[0_4px_20px_rgba(61,64,91,0.06)]"
      >
        <p data-testid="quiz-result-narrative" className="text-ink text-base sm:text-lg leading-relaxed font-sans">
          {profile.narrative}
        </p>
      </motion.div>

      <div className="mt-8 flex flex-col sm:flex-row gap-3">
        <Button
          data-testid="quiz-result-primary"
          onClick={() => {
            if (profile.key === "worried") navigate("/parlarne");
            else navigate("/confronta");
          }}
          className="rounded-full h-12 bg-terracotta hover:bg-terracotta-hover text-white font-sans font-bold flex-1"
        >
          {profile.cta_primary}
        </Button>
        <Button
          data-testid="quiz-result-secondary"
          variant="outline"
          onClick={() => navigate("/esplora")}
          className="rounded-full h-12 border-terracotta text-terracotta hover:bg-terracotta/5 font-sans font-semibold flex-1"
        >
          {profile.cta_secondary}
        </Button>
      </div>

      {(profile.key === "curious" || profile.key === "worried") && (
        <div className="mt-6 text-center">
          <button
            type="button"
            data-testid="quiz-result-preview-organize"
            onClick={() => navigate("/organizza")}
            className="text-sm text-ink-soft hover:text-terracotta underline-offset-4 hover:underline transition-colors font-sans"
          >
            {t.quiz.previewOrganize}
          </button>
        </div>
      )}
    </div>
  );
}
